import { useState } from "react";
import { ClipboardCheck, Smartphone, Shield, CheckCircle2, Banknote } from "lucide-react";
import { CORRIDORS } from "@/lib/constants";

const STEPS_BY_CORRIDOR = {
  tg_ga: [
    { n: "01", t: "Saisissez l'envoi", d: "Bénéficiaire, montant et numéro Mobile Money au Gabon.", icon: ClipboardCheck },
    { n: "02", t: "Payez Moov / TMoney", d: "Dépôt depuis votre Moov ou TMoney Togo, conservez la référence.", icon: Smartphone },
    { n: "03", t: "Vérification manuelle", d: "Notre équipe valide votre dépôt en moins d'une heure.", icon: Shield },
    { n: "04", t: "Argent reçu au Gabon", d: "Le bénéficiaire reçoit sur Airtel ou Moov Money Gabon.", icon: CheckCircle2 },
  ],
  ga_fr: [
    { n: "01", t: "Saisissez l'envoi", d: "Email PayPal, IBAN ou Wero du bénéficiaire en France.", icon: ClipboardCheck },
    { n: "02", t: "Payez Airtel Gabon", d: "Dépôt Airtel Money sur le numéro Swiftlink, conservez la capture.", icon: Smartphone },
    { n: "03", t: "Vérification manuelle", d: "Notre équipe valide votre dépôt en moins d'une heure.", icon: Shield },
    { n: "04", t: "Euros reçus en France", d: "Crédité sur PayPal, Wero ou par virement SEPA instantané.", icon: Banknote },
  ],
  ga_rw: [
    { n: "01", t: "Saisissez l'envoi", d: "Numéro MTN, Tigo ou Airtel Money Rwanda du bénéficiaire.", icon: ClipboardCheck },
    { n: "02", t: "Payez Airtel Gabon", d: "Dépôt Airtel Money sur le numéro Swiftlink, conservez la capture.", icon: Smartphone },
    { n: "03", t: "Vérification manuelle", d: "Notre équipe valide votre dépôt en moins d'une heure.", icon: Shield },
    { n: "04", t: "Francs reçus au Rwanda", d: "Crédité sur MTN, Tigo ou Airtel Money Rwanda en RWF.", icon: CheckCircle2 },
  ],
  fr_ga: [
    { n: "01", t: "Saisissez l'envoi", d: "Numéro Airtel ou Moov Money Gabon du bénéficiaire.", icon: ClipboardCheck },
    { n: "02", t: "Payez PayPal F&F", d: "Mode « Entre proches » obligatoire pour éviter les frais PayPal.", icon: Banknote },
    { n: "03", t: "Vérification manuelle", d: "Notre équipe confirme le paiement PayPal en moins d'une heure.", icon: Shield },
    { n: "04", t: "FCFA reçus au Gabon", d: "Le bénéficiaire reçoit l'équivalent à 1 € = 660 FCFA.", icon: CheckCircle2 },
  ],
};

/**
 * "Comment ça marche" — 4-step explainer with corridor tabs.
 * Same 4-step skeleton but icons + copy adapt to the selected corridor.
 */
export default function HowItWorks() {
  const [corridor, setCorridor] = useState("tg_ga");
  const steps = STEPS_BY_CORRIDOR[corridor] || STEPS_BY_CORRIDOR.tg_ga;

  return (
    <section className="py-20 bg-white border-y border-gray-200" id="comment-ca-marche">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Comment ça marche</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">Quatre étapes, zéro friction.</h2>
          <p className="mt-3 text-gray-600 text-sm">Sélectionnez votre corridor pour voir les étapes adaptées.</p>
        </div>

        {/* Corridor selector */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex flex-wrap justify-center gap-1 p-1 bg-[var(--sl-bg-alt)] rounded-full border border-gray-200" data-testid="how-corridor-tabs">
            {Object.values(CORRIDORS).map((co) => (
              <button
                key={co.id}
                onClick={() => setCorridor(co.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  corridor === co.id
                    ? "bg-[var(--sl-primary)] text-white shadow-sm"
                    : "text-gray-700 hover:text-[var(--sl-primary)]"
                }`}
                data-testid={`how-corridor-tab-${co.id}`}
              >
                {co.flagFrom} {co.short} {co.flagTo}
              </button>
            ))}
          </div>
        </div>

        <div key={corridor} className="mt-12 grid md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={s.n} className="sl-card sl-card-hoverable sl-rotate-in" style={{ animationDelay: `${i * 60}ms` }} data-testid={`step-card-${corridor}-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-gray-400">{s.n}</span>
                <s.icon size={20} className="text-[var(--sl-primary)]" />
              </div>
              <h3 className="font-heading text-xl font-semibold mt-4">{s.t}</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
